import * as XLSX from "xlsx";
import React, { useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

interface ImportExcelButtonProps {
  backendUrl: string;
  userid: string | number;
  id_site?: number | null;
  onSuccess?: () => void;
}

const ImportExcelButton: React.FC<ImportExcelButtonProps> = ({
  backendUrl,
  userid,
  id_site,
  onSuccess,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [importedPrograms, setImportedPrograms] = useState<any[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);


  const formatDate = (value: any) => {
  if (typeof value === "number") {
    const jsDate = new Date((value - 25569) * 86400 * 1000);
    return jsDate.toISOString().slice(0, 10);
  }
  return value;
};


  // --- Lecture du fichier Excel ---
const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setImportedPrograms([]);
  setSelectedPrograms(new Set());
  setCurrentPage(1);

  const reader = new FileReader();
  reader.onload = (evt) => {
    const data = evt.target?.result;
    if (!data) return;

    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const raw: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

    const finalData: any[] = [];
    let currentClient = "";

    raw.forEach((row) => {
      const v0 = (row[0] || "").toString().trim();

      // détection du nom client
      if (
        v0 &&
        row.length === 1 &&
        !v0.match(/DATE|CHAUFFEUR|DEPART|TYPE|N°|IMMAT/i)
      ) {
        currentClient = v0;
        return;
      }

      if (!v0 || v0 === "DATE" || v0 === "Date") return;
      if (row.length < 5) return;

      // Extraire dynamiquement OBS + OBS2
      let dynamicValues = [];

      for (let i = 6; i < row.length; i++) {
        const cell = row[i] ? row[i].toString().trim() : "";
        if (!cell) continue;

        // ignorer titres
        if (/^(OBS|KLM|CLIENT|CONTACT|IMM|DECHARGEMENT)$/i.test(cell)) continue;

        dynamicValues.push(cell);
      }

      const observation = dynamicValues[0] || "";
      const observation2 = dynamicValues[1] || "";

      finalData.push({
        "Program Date": formatDate(row[0]),     // ON LAISSE LA DATE TELLE QUELLE
        "Client": currentClient,
        "Driver": row[1] || "",
        "Vehicle": row[2] || "",
        "Departure": row[3] || "",
        "Destination": row[4] || "",
        "Type": row[5] || "",
        "Observation": observation,
        "Observation2": observation2,
        "id_site": id_site,
        _uuid: uuidv4()
      });
    });

    setImportedPrograms(finalData);
    setSelectedPrograms(new Set(finalData.map((p) => p._uuid)));
    setShowModal(true);
  };

  reader.readAsArrayBuffer(file);
  e.target.value = "";
};


  // --- Import ---
const handleImport = async () => {
  if (!importedPrograms.length || !selectedPrograms.size) return;
  setLoading(true);

  try {
    const programsToImport = importedPrograms.filter((p) =>
      selectedPrograms.has(p._uuid)
    );

    for (const p of programsToImport) {
      await fetch(`${backendUrl}/api/program/create/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          programData: {
            ...p,
            id_site,
            id_client: p.Client || null,
            id_conducteur: p.Driver || null,
            id_vehicle: p.Vehicle || null,
            id_departure: p.Departure || null,
            observation: p.Observation || "",
            observation2: p.Observation2 || "",
            type: p.Type || "",
            program_date: p["Program Date"],  // LA DATE EXACTE COMME DEMANDÉ
            destination: p.Destination || ""
          }
        }),
      });
    }

    setShowModal(false);
    setImportedPrograms([]);
    setSelectedPrograms(new Set());
    setCurrentPage(1);
    if (onSuccess) onSuccess();
  } catch (error) {
    console.error("Error importing programs:", error);
    alert("Import failed. Check console for details.");
  } finally {
    setLoading(false);
  }
};

  const totalPages = Math.ceil(importedPrograms.length / pageSize);
  const displayedPrograms = importedPrograms.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const toggleSelectAllCurrentPage = (checked: boolean) => {
    setSelectedPrograms((prevSet) => {
      const newSet = new Set(prevSet);
      displayedPrograms.forEach((p) => {
        if (checked) newSet.add(p._uuid);
        else newSet.delete(p._uuid);
      });
      return newSet;
    });
  };

  const toggleSelectAllTotal = (checked: boolean) => {
    setSelectedPrograms(() => {
      if (checked) {
        return new Set(importedPrograms.map((p) => p._uuid));
      } else {
        return new Set();
      }
    });
  };

  return (
    <>
      <label className="btn btn-outline-primary mr-2 mb-0">
        Import Excel
        <input type="file" accept=".xls,.xlsx" hidden onChange={handleFileChange} />
      </label>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        dialogClassName="modal-fullscreen"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Programs to Import</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ overflowY: "auto" }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <label>
                Rows per page:{" "}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div>
              <label>
                <input
                  type="checkbox"
                  checked={importedPrograms.length > 0 && selectedPrograms.size === importedPrograms.length}
                  onChange={(e) => toggleSelectAllTotal(e.target.checked)}
                />{" "}
                Select All
              </label>
            </div>

            {totalPages > 1 && <div>Page {currentPage} / {totalPages}</div>}
          </div>

          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={importedPrograms.length > 0 && selectedPrograms.size === importedPrograms.length}
                    onChange={(e) => toggleSelectAllTotal(e.target.checked)}
                  />{" "}
                </th>
                {importedPrograms.length > 0 &&
                  Object.keys(importedPrograms[0])
                    .filter((key) => key !== "_uuid")
                    .map((key) => (
                      <th key={key}>{key}</th>
                    ))}
              </tr>
            </thead>
            <tbody>
              {displayedPrograms.map((p) => (
                <tr key={p._uuid}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedPrograms.has(p._uuid)}
                      onChange={(e) => {
                        setSelectedPrograms((prevSet) => {
                          const newSet = new Set(prevSet);
                          if (e.target.checked) newSet.add(p._uuid);
                          else newSet.delete(p._uuid);
                          return newSet;
                        });
                      }}
                    />
                  </td>
                  {Object.entries(p)
                    .filter(([key]) => key !== "_uuid")
                    .map(([_, val], i) => (
                      <td key={i}>{String(val)}</td>
                    ))}
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between mt-2 align-items-center">
              <Button
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} / {totalPages}
              </span>
              <Button
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </Button>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleImport} disabled={loading}>
            {loading ? "Importing..." : "Import Selected"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImportExcelButton;
